import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { formatErrorList } from '../../shared/alert/format-error-list';
import { TranslateService } from '@ngx-translate/core';
import { HttpResponse } from '@angular/common/http';
import { Principal } from '../../shared';
import { AlertService } from '../../shared/alert/alert.service';
import { isNumeric } from '../../shared/util/is-numeric';
import { JhiEventManager } from '../../shared/core-utils';
import { LotService } from '../../shared/inventory/service/lot.service';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';

const SUCCESS = 'inventoryManagerApp.lot.import.balance.success';

@Component({
    selector: 'jhi-lot-import-update-balance-mapping',
    templateUrl: './lot-import-update-balance-mapping.dialog.component.html'
})

export class LotImportUpdateBalanceMappingDialogComponent implements OnInit, OnDestroy {

    @Input() importData: Array<Array<any>>;
    header: Array<string>;
    data: Array<any>;
    lotUIDMapping;
    newBalanceMapping;
    storageLocationAbbrMapping;
    notesMapping;
    isProcessing = false;
    storageLocationAbbrLabelCheck: any;
    notesLabelCheck: any;
    subscriptionsManager = new SubscriptionsManager();

    constructor(private alertService: AlertService,
                private modal: NgbActiveModal,
                private modalService: NgbModal,
                private lotService: LotService,
                private eventManager: JhiEventManager,
                private translateService: TranslateService,
                private principal: Principal) {
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.header = this.importData[0].map(this.toUpper);

        // Convert rawData into map
        this.data = this.importData.slice(1).map((fileRow, rowIndex) => {
            return fileRow.reduce((map, col, colIndex) => {
                map[this.header [colIndex]] = col;
                return map;
            }, {});
        });

        this.lotUIDMapping = this.mappingHeader(this.importData[0], 'Lot UID');
        this.newBalanceMapping = this.mappingHeader(this.importData[0], 'New Balance');
        this.storageLocationAbbrMapping = this.mappingHeader(this.importData[0], 'Storage Location Abbr');
        this.notesMapping = this.mappingHeader(this.importData[0], 'Notes');

    }

    mappingHeader(header: Array<string>, mapping: string) {
        for (const column of header) {
            if (column.toLowerCase() === mapping.toLowerCase()) {
                return column;
            }
        }
        return '';
    }

    back() {
        this.modal.dismiss();
    }

    closed() {
        this.modal.dismiss();
    }

    async proceed(f) {
        if (this.validate(f)) {
            this.isProcessing = true;
            const lotUpdateBalanceRequest = this.importData
                .slice(1) // skip header
                .filter((row) => row.some((column) => Boolean(column.trim()))) // remove empty rows
                .map((row, index) => {
                    return Object.assign({
                        lotUUID: row[this.getLotUIDColumnIndex()],
                        notes: !f.form.controls.dropdownNotes.disabled ? row[this.getNotesColumnIndex()] : null,
                        storageLocationAbbr: !f.form.controls.dropdownLocationAbbreviation.disabled ? row[this.getStorageLocationAbbrColumnIndex()] : null,
                        balance: row[this.getNewBalanceColumnIndex()]
                    });
                });

            const lotUUIDs = lotUpdateBalanceRequest.map((lot) => lot.lotUUID);
            this.subscriptionsManager.add = this.lotService.updateBalance(lotUpdateBalanceRequest).subscribe(
                (res: HttpResponse<any>) =>
                    this.onSaveSuccess(lotUUIDs, SUCCESS));
        }
    }

    private validateHeader(errorMessage: string[]) {
        const duplicateColumns = this.header.filter((header, i) => this.header.indexOf(header) !== i);
        if (duplicateColumns.length > 1) {
            errorMessage.push(this.translateService.instant('error.import.header.duplicated', { param: duplicateColumns.join(', ') }));
        }
    }

    toUpper(param: string) {
        return (param || '').toUpperCase();
    }

    private validate(f) {
        const errorMessage: string[] = [];
        this.validateHeader(errorMessage);
        this.validateData(this.importData.slice(1), errorMessage, f);

        if (errorMessage.length !== 0) {
            this.alertService.error('error.custom', { param: formatErrorList(errorMessage) });
            return false;
        }
        return true;
    }

    private validateData(importData: any[], errorMessage: string[], f) {
        for (let i = 0; i < importData.length; i++) {
            const row = importData[i];
            // Lot UID
            if (!row[this.getLotUIDColumnIndex()]) {
                errorMessage.push(this.translateService.instant('inventoryManagerApp.lot.import.balance.validation.lotUID'));
                break;
            }
            // New Balance
            if (!row[this.getNewBalanceColumnIndex()] || !isNumeric(row[this.getNewBalanceColumnIndex()])) {
                errorMessage.push(this.translateService.instant('inventoryManagerApp.lot.import.balance.validation.newBalance'));
                break;
            }
            // Storage Location Abbr
            if (!f.form.controls.dropdownLocationAbbreviation.disabled && !row[this.getStorageLocationAbbrColumnIndex()]) {
                errorMessage.push(this.translateService.instant('inventoryManagerApp.lot.import.balance.validation.storageLocationAbbr'));
                break;
            }
            // Notes
            if (!f.form.controls.dropdownNotes.disabled && row[this.getNotesColumnIndex()] && row[this.getNotesColumnIndex()].length > 255) {
                errorMessage.push(this.translateService.instant('inventoryManagerApp.lot.import.balance.validation.notes'));
                break;
            }
        }
    }

    private getLotUIDColumnIndex() {
        return this.header.indexOf(this.lotUIDMapping);
    }

    private getNotesColumnIndex() {
        return this.header.indexOf(this.notesMapping);
    }

    private getNewBalanceColumnIndex() {
        return this.header.indexOf(this.newBalanceMapping);
    }

    private getStorageLocationAbbrColumnIndex() {
        return this.header.indexOf(this.storageLocationAbbrMapping);
    }

    private onSaveSuccess(lotUUIDs, message) {
        this.alertService.success(message);
        this.eventManager.broadcast({ name: 'columnFiltersChanged', content: { filterBy: { lotUUIDs } } });
        this.modal.close('importUpdated');
    }

    isFormValid(f) {
        const form = f.form;
        if (Object.values(form.controls).filter((control: any) => !control.disabled).length === 1) {
            return false;
        }
        return f.form.valid;
    }

    isLotUIDMapped() {
        return this.lotUIDMapping;
    }

    isNewBalanceMapped() {
        return this.newBalanceMapping;
    }
}
